export type ChromeCvdLocale = 'en_US' | 'es_US'

export type VehicleStatusFacetOption = {
  value: string
  label: string
}

export type InventoryAlertsChannel = 'email' | 'sms'

export type InventoryAlertsRecipient = {
  email?: string
  phone?: string
}

export type InventoryAlertsEventSettings = {
  enabled: boolean
  offsetsDays: number[]
  thresholdDaysByCondition: {
    new?: number
    used?: number
    cpo?: number
  }
}

export type InventoryAlertsSettings = {
  enabled: boolean
  channels: InventoryAlertsChannel[]
  recipients: InventoryAlertsRecipient[]
  events: Record<string, InventoryAlertsEventSettings>
}

export const MANUAL_VEHICLE_EXCEEDED_TIME_TO_SALE =
  'manual_vehicle.exceeded_time_to_sale' as const

export function defaultInventoryAlertsSettings(): InventoryAlertsSettings {
  return {
    enabled: true,
    channels: ['email'],
    recipients: [],
    events: {
      [MANUAL_VEHICLE_EXCEEDED_TIME_TO_SALE]: {
        enabled: true,
        offsetsDays: [0, 10],
        thresholdDaysByCondition: { new: 30, used: 28, cpo: 28 },
      },
    },
  }
}

export function normalizeInventoryAlertsSettings(raw: unknown): InventoryAlertsSettings {
  const defaults = defaultInventoryAlertsSettings()
  const record = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {}
  const eventsRaw =
    record.events && typeof record.events === 'object' && !Array.isArray(record.events)
      ? (record.events as Record<string, unknown>)
      : {}
  const eventRaw = eventsRaw[MANUAL_VEHICLE_EXCEEDED_TIME_TO_SALE]
  const eventRecord =
    eventRaw && typeof eventRaw === 'object' && !Array.isArray(eventRaw)
      ? (eventRaw as Record<string, unknown>)
      : {}
  const thresholdsRaw =
    eventRecord.thresholdDaysByCondition &&
    typeof eventRecord.thresholdDaysByCondition === 'object' &&
    !Array.isArray(eventRecord.thresholdDaysByCondition)
      ? (eventRecord.thresholdDaysByCondition as Record<string, unknown>)
      : {}
  const channels = Array.isArray(record.channels)
    ? (record.channels.filter((channel): channel is InventoryAlertsChannel =>
        channel === 'email' || channel === 'sms',
      ))
    : [...defaults.channels]
  const uniqueChannels = Array.from(new Set(channels))
  uniqueChannels.sort((a, b) => a.localeCompare(b))
  const offsets = Array.isArray(eventRecord.offsetsDays)
    ? eventRecord.offsetsDays.filter((n): n is number => typeof n === 'number' && Number.isFinite(n) && n >= 0)
    : [...defaults.events[MANUAL_VEHICLE_EXCEEDED_TIME_TO_SALE].offsetsDays]
  const recipients = Array.isArray(record.recipients)
    ? record.recipients.flatMap((item) => {
        if (!item || typeof item !== 'object') return []
        const row = item as Record<string, unknown>
        const email = typeof row.email === 'string' ? row.email.trim() : ''
        const phone = typeof row.phone === 'string' ? row.phone.trim() : ''
        if (!email && !phone) return []
        return [{ ...(email ? { email } : {}), ...(phone ? { phone } : {}) }]
      })
    : []
  const defaultEvent = defaults.events[MANUAL_VEHICLE_EXCEEDED_TIME_TO_SALE]
  return {
    enabled: record.enabled !== false,
    channels: uniqueChannels.length > 0 ? uniqueChannels : ['email'],
    recipients,
    events: {
      [MANUAL_VEHICLE_EXCEEDED_TIME_TO_SALE]: {
        enabled: eventRecord.enabled !== false,
        offsetsDays: (offsets.length > 0 ? offsets : defaultEvent.offsetsDays).slice().sort((a, b) => a - b),
        thresholdDaysByCondition: {
          new:
            typeof thresholdsRaw.new === 'number' && thresholdsRaw.new > 0
              ? thresholdsRaw.new
              : defaultEvent.thresholdDaysByCondition.new,
          used:
            typeof thresholdsRaw.used === 'number' && thresholdsRaw.used > 0
              ? thresholdsRaw.used
              : defaultEvent.thresholdDaysByCondition.used,
          cpo:
            typeof thresholdsRaw.cpo === 'number' && thresholdsRaw.cpo > 0
              ? thresholdsRaw.cpo
              : defaultEvent.thresholdDaysByCondition.cpo,
        },
      },
    },
  }
}

export function inventoryAlertsSettingsEqual(
  left: InventoryAlertsSettings,
  right: InventoryAlertsSettings,
): boolean {
  return JSON.stringify(normalizeInventoryAlertsSettings(left)) === JSON.stringify(normalizeInventoryAlertsSettings(right))
}

export type IseSettingsResponse = {
  primarySources: string[]
  pause_publish_by_feed?: boolean
  feed_guardrail_enabled?: boolean
  max_allowed_net_change_percent?: number
  chrome_cvd_locale?: ChromeCvdLocale
  separate_ready_courtesy_vehicles?: boolean
  vehicleStatusFacet?: VehicleStatusFacetOption[]
  inventoryAlerts?: InventoryAlertsSettings
  inventoryAlertsProductAssigned?: boolean
  source: 'draft' | 'published' | 'none'
  savedAt?: string
  savedBy?: string
}

export async function fetchIsePrimarySources(
  endpoint: string,
  jazelId: string,
  opts?: { includeDraft?: boolean; token?: string },
): Promise<IseSettingsResponse> {
  const base = String(endpoint || '').replace(/\/+$/g, '')
  const url = new URL(`${base}/ise-settings/${encodeURIComponent(jazelId)}`)
  if (opts?.includeDraft) url.searchParams.set('includeDraft', 'true')

  const headers: Record<string, string> = {}
  const token = String(opts?.token || '').trim()
  if (token) headers.Authorization = `Bearer ${token}`

  const resp = await fetch(url.toString(), { headers })
  if (!resp.ok) throw new Error(`Failed to load ISE primary sources (${resp.status})`)
  const json = (await resp.json().catch(() => ({}))) as any
  const data = (json?.data || {}) as IseSettingsResponse
  return data
}

export async function fetchIsePrimarySourcesDraftSummary(
  endpoint: string,
  jazelId: string,
  opts?: { token?: string },
): Promise<{ hasDraft: boolean; savedAt?: string; savedBy?: string }> {
  const base = String(endpoint || '').replace(/\/+$/g, '')
  const url = new URL(`${base}/ise-settings/${encodeURIComponent(jazelId)}`)
  url.searchParams.set('action', 'draftSummary')

  const headers: Record<string, string> = {}
  const token = String(opts?.token || '').trim()
  if (token) headers.Authorization = `Bearer ${token}`

  const resp = await fetch(url.toString(), { headers })
  if (!resp.ok) throw new Error(`Failed to load ISE primary sources draft summary (${resp.status})`)
  const json = (await resp.json().catch(() => ({}))) as any
  return (json?.data || {}) as any
}

export async function saveIsePrimarySourcesDraft(
  endpoint: string,
  jazelId: string,
  primarySources: string[],
  opts?: { token?: string },
): Promise<void> {
  const base = String(endpoint || '').replace(/\/+$/g, '')
  const url = new URL(`${base}/ise-settings/${encodeURIComponent(jazelId)}`)

  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = String(opts?.token || '').trim()
  if (token) headers.Authorization = `Bearer ${token}`

  const resp = await fetch(url.toString(), {
    method: 'PUT',
    headers,
    body: JSON.stringify({ primarySources }),
  })

  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    throw new Error(`Failed to save ISE primary sources draft (${resp.status}): ${text || 'unknown error'}`)
  }
}

export async function saveIsePausePublishByFeedDraft(
  endpoint: string,
  jazelId: string,
  pause_publish_by_feed: boolean,
  opts?: { token?: string },
): Promise<void> {
  const base = String(endpoint || '').replace(/\/+$/g, '')
  const url = new URL(`${base}/ise-settings/${encodeURIComponent(jazelId)}`)

  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = String(opts?.token || '').trim()
  if (token) headers.Authorization = `Bearer ${token}`

  const resp = await fetch(url.toString(), {
    method: 'PUT',
    headers,
    body: JSON.stringify({ pause_publish_by_feed: !!pause_publish_by_feed }),
  })

  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    throw new Error(`Failed to save ISE settings draft (${resp.status}): ${text || 'unknown error'}`)
  }
}

export async function saveIseGuardrailSettingsDraft(
  endpoint: string,
  jazelId: string,
  settings: { feed_guardrail_enabled: boolean; max_allowed_net_change_percent: number },
  opts?: { token?: string },
): Promise<void> {
  const base = String(endpoint || '').replace(/\/+$/g, '')
  const url = new URL(`${base}/ise-settings/${encodeURIComponent(jazelId)}`)

  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = String(opts?.token || '').trim()
  if (token) headers.Authorization = `Bearer ${token}`

  const resp = await fetch(url.toString(), {
    method: 'PUT',
    headers,
    body: JSON.stringify({
      feed_guardrail_enabled: settings.feed_guardrail_enabled,
      max_allowed_net_change_percent: settings.max_allowed_net_change_percent,
    }),
  })

  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    throw new Error(`Failed to save ISE guardrail settings draft (${resp.status}): ${text || 'unknown error'}`)
  }
}

export async function saveIseChromeCvdLocaleDraft(
  endpoint: string,
  jazelId: string,
  chrome_cvd_locale: ChromeCvdLocale,
  opts?: { token?: string },
): Promise<void> {
  const base = String(endpoint || '').replace(/\/+$/g, '')
  const url = new URL(`${base}/ise-settings/${encodeURIComponent(jazelId)}`)

  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = String(opts?.token || '').trim()
  if (token) headers.Authorization = `Bearer ${token}`

  const resp = await fetch(url.toString(), {
    method: 'PUT',
    headers,
    body: JSON.stringify({ chrome_cvd_locale }),
  })

  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    throw new Error(`Failed to save Chrome CVD locale draft (${resp.status}): ${text || 'unknown error'}`)
  }
}

export async function saveIseInventoryAlertsDraft(
  endpoint: string,
  jazelId: string,
  inventoryAlerts: InventoryAlertsSettings,
  opts?: { token?: string },
): Promise<void> {
  const base = String(endpoint || '').replace(/\/+$/g, '')
  const url = new URL(`${base}/ise-settings/${encodeURIComponent(jazelId)}`)

  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = String(opts?.token || '').trim()
  if (token) headers.Authorization = `Bearer ${token}`

  const resp = await fetch(url.toString(), {
    method: 'PUT',
    headers,
    body: JSON.stringify({ inventoryAlerts: normalizeInventoryAlertsSettings(inventoryAlerts) }),
  })

  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    throw new Error(`Failed to save inventory alerts draft (${resp.status}): ${text || 'unknown error'}`)
  }
}

export async function saveIseReadyCourtesyStateDraft(
  endpoint: string,
  jazelId: string,
  separate_ready_courtesy_vehicles: boolean,
  opts?: { token?: string },
): Promise<void> {
  const base = String(endpoint || '').replace(/\/+$/g, '')
  const url = new URL(`${base}/ise-settings/${encodeURIComponent(jazelId)}`)

  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = String(opts?.token || '').trim()
  if (token) headers.Authorization = `Bearer ${token}`

  const resp = await fetch(url.toString(), {
    method: 'PUT',
    headers,
    body: JSON.stringify({ separate_ready_courtesy_vehicles }),
  })

  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    throw new Error(
      `Failed to save Ready Courtesy state setting (${resp.status}): ${text || 'unknown error'}`,
    )
  }
}

export async function saveVehicleStatusFacetDraft(
  endpoint: string,
  jazelId: string,
  vehicleStatusFacet: VehicleStatusFacetOption[],
  opts?: { token?: string },
): Promise<void> {
  const base = String(endpoint || '').replace(/\/+$/g, '')
  const url = new URL(`${base}/ise-settings/${encodeURIComponent(jazelId)}`)

  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = String(opts?.token || '').trim()
  if (token) headers.Authorization = `Bearer ${token}`

  const resp = await fetch(url.toString(), {
    method: 'PUT',
    headers,
    body: JSON.stringify({ vehicleStatusFacet }),
  })

  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    throw new Error(`Failed to save Vehicle Status facet draft (${resp.status}): ${text || 'unknown error'}`)
  }
}

export type IseSettingsSection =
  | 'feedSourceConfig'
  | 'pausePublish'
  | 'guardrail'
  | 'chromeCvdLocale'
  | 'readyCourtesyState'
  | 'vehicleStatusFacet'
  | 'inventoryAlerts'

/**
 * Reverts a single section of the iseSettings draft. A `section` is REQUIRED:
 * the backend rejects section-less reverts with 400 (they would ambiguously wipe
 * sibling sections). `primarySources` is not a valid section — it is derived at
 * publish time.
 */
export async function deleteIseSettingsDraft(
  endpoint: string,
  jazelId: string,
  opts: { section: IseSettingsSection; token?: string },
): Promise<void> {
  const base = String(endpoint || '').replace(/\/+$/g, '')
  const url = new URL(`${base}/ise-settings/${encodeURIComponent(jazelId)}`)
  url.searchParams.set('section', opts.section)

  const headers: Record<string, string> = {}
  const token = String(opts?.token || '').trim()
  if (token) headers.Authorization = `Bearer ${token}`

  const resp = await fetch(url.toString(), {
    method: 'DELETE',
    headers,
  })

  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    throw new Error(`Failed to revert ISE settings section (${resp.status}): ${text || 'unknown error'}`)
  }
}

